import React, {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {MarketplaceOnly} from '@udemy/react-brand-components';
import {
    ENTRI_LISTING_BASE_URL,
    resolveListingBannerFromSubcategoryId,
    VernacularCohortCategoryBanner,
    VernacularCohortTopicBanner,
} from '@udemy/react-globalization-components/vernacular-cohort';

import styles from './vernacular-cohort-browse-banner.module.less';

type VernacularCohortBrowseBannerProps =
    | {
          page: 'category';
          udemyCategoryId: number;
      }
    | {
          page: 'subcategory';
          subcategoryId: number;
          /** Parent category id (`pageObject.category_id`). Used when the subcategory is not mapped. */
          parentCategoryId?: number;
      }
    | {
          page: 'topic';
          topicId: number | string;
      };

/**
 * Category, subcategory, and topic placement for the vernacular cohort banner.
 * Mapped browse pages use the listing creative. Every other page shows the generic creative
 * (`unmappedFallback="generic"`), so unmapped ids need no special casing here.
 * Marketplace only: hidden on organization (UB) sites.
 */
export const VernacularCohortBrowseBanner = (props: VernacularCohortBrowseBannerProps) => {
    const {gettext} = useI18n();

    const udemyCategoryId = useMemo(() => {
        if (props.page === 'category') {
            return props.udemyCategoryId;
        }
        if (props.page === 'subcategory') {
            return (
                resolveListingBannerFromSubcategoryId(props.subcategoryId, gettext)
                    ?.udemyCategoryId ?? props.parentCategoryId
            );
        }
        return undefined;
    }, [gettext, props]);

    if (props.page === 'topic') {
        // The banner takes numeric ids, so page data is converted and validated here. An unusable
        // id means there is nothing to identify the page by, so there is nothing to show or track.
        const topicId = Number(props.topicId);
        if (!Number.isSafeInteger(topicId) || topicId <= 0) {
            return null;
        }

        return (
            <MarketplaceOnly>
                <div className={styles.banner}>
                    <VernacularCohortTopicBanner
                        topicId={topicId}
                        unmappedFallback="generic"
                        entriListingBaseUrl={ENTRI_LISTING_BASE_URL}
                    />
                </div>
            </MarketplaceOnly>
        );
    }

    if (udemyCategoryId === undefined) {
        // No category id to identify the page by, so there is nothing to show or track.
        return null;
    }

    return (
        <MarketplaceOnly>
            <div className={styles.banner}>
                <VernacularCohortCategoryBanner
                    categoryId={udemyCategoryId}
                    unmappedFallback="generic"
                    entriListingBaseUrl={ENTRI_LISTING_BASE_URL}
                />
            </div>
        </MarketplaceOnly>
    );
};
